import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIST_CHANNELS = [
  "Tiendas agroquímicas",
  "Cooperativas",
  "Venta directa al agricultor",
  "Online",
  "Otro",
];
const VOLUME_RANGES = [
  "< 500 kg / mes",
  "500 - 2.000 kg / mes",
  "2.000 - 10.000 kg / mes",
  "10.000 - 50.000 kg / mes",
  "+ 50.000 kg / mes",
];
const YEARS = ["< 1 año", "1 - 3 años", "3 - 5 años", "5 - 10 años", "+ 10 años"];
const HOW = ["Google", "LinkedIn", "Instagram", "WhatsApp", "Recomendación", "Feria / Evento", "Otro"];

const BENEFITS = [
  { icon: "fa-map-location-dot", title: "Territorio exclusivo por región", text: "Protegemos tu inversión con exclusividad geográfica negociable según volumen." },
  { icon: "fa-graduation-cap", title: "Formación técnica agronómica", text: "Capacitación continua sobre formulaciones, dosis y protocolos de aplicación por cultivo." },
  { icon: "fa-folder-tree", title: "Material de marketing profesional", text: "Catálogos, fichas técnicas, fotos y vídeos listos para usar en tu canal comercial." },
  { icon: "fa-industry", title: "Precio directo desde fábrica", text: "Sin intermediarios. Margen superior y condiciones flexibles según volumen mensual." },
];

/**
 * Reusable Distribuidores content. Renders intro, benefits, and form.
 * Used by the inline section <Distribuidores /> and the modal <DistribuidoresModal />.
 */
export function DistribuidoresContent({ variant = "section", onClose }) {
  const isModal = variant === "modal";
  const [countries, setCountries] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", country: "", company_name: "",
    company_website: "", years_in_sector: "", current_portfolio: "",
    distribution_channels: [], coverage_zone: "", estimated_volume_kg: "",
    how_did_you_know: "", extra_message: "",
  });

  useEffect(() => {
    axios.get(`${API}/countries`).then(r => setCountries(r.data.countries || [])).catch(() => {});
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleChannel = (ch) => {
    const arr = form.distribution_channels.includes(ch)
      ? form.distribution_channels.filter(c => c !== ch)
      : [...form.distribution_channels, ch];
    setForm({ ...form, distribution_channels: arr });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/distributor-apply`, form);
      setSubmitted(true);
      toast.success("Solicitud enviada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full bg-white rounded-xl px-4 py-3 text-sm outline-none border border-[#e5e8e2] focus:border-[#133322] transition";
  const labelCls = "eu-data-label block mb-2";

  return (
    <div className={isModal ? "p-6 sm:p-10" : ""} data-testid={isModal ? "distribuidores-modal-content" : undefined}>
      {/* Header / Intro */}
      <div className={`${isModal ? "" : "max-w-3xl mx-auto text-center mb-16"}`}>
        <div className={`eu-data-label mb-4 inline-flex items-center ${isModal ? "" : ""}`}>
          <i className="fa-solid fa-handshake mr-2"></i>
          DISTRIBUIDORES {isModal ? "" : "· 06"}
        </div>
        <h2 className={`font-display ${isModal ? "text-3xl lg:text-4xl text-left" : "text-4xl lg:text-6xl text-center"} text-[#133322] leading-[1.05] tracking-tight`}>
          {isModal ? (
            <>Distribuye <em className="text-[#b45f3e] not-italic">EUZERONE</em> en tu región</>
          ) : (
            <>¿Listo para llevar <em className="text-[#b45f3e] not-italic">EUZERONE</em> a tu mercado?</>
          )}
        </h2>
        <p className={`mt-5 ${isModal ? "text-sm text-left max-w-2xl" : "text-base lg:text-lg text-center mt-6"} text-[#5c6b61] font-light`}>
          Buscamos socios en Europa, Hispanoamérica y mercado hispano de EE.UU. Únete a una marca con base científica europea y respaldo agronómico continuo.
        </p>
      </div>

      {/* Benefits */}
      <div className={`grid sm:grid-cols-2 ${isModal ? "lg:grid-cols-2 gap-3 my-8" : "lg:grid-cols-4 gap-4 mb-16"}`}>
        {BENEFITS.map((b, i) => (
          <article
            key={i}
            className={`${isModal ? "bg-[#f7f7f4] p-5" : "eu-card p-7 hover:-translate-y-1"} rounded-2xl transition group`}
            data-testid={`dist-benefit-${i}`}
          >
            <div className={`${isModal ? "h-9 w-9 mb-3" : "h-12 w-12 mb-5"} rounded-full bg-[#b45f3e]/15 border border-[#b45f3e]/20 flex items-center justify-center`}>
              <i className={`fa-solid ${b.icon} text-[#b45f3e] ${isModal ? "text-sm" : ""}`}></i>
            </div>
            <h3 className={`font-display ${isModal ? "text-base" : "text-xl"} text-[#133322] leading-tight`}>{b.title}</h3>
            {!isModal && <p className="mt-3 text-sm text-[#5c6b61] leading-relaxed">{b.text}</p>}
          </article>
        ))}
      </div>

      {/* Form / Success */}
      {submitted ? (
        <div className={`${isModal ? "" : "eu-card max-w-2xl mx-auto"} p-10 lg:p-16 text-center`} data-testid="distribuidores-success">
          <div className="h-16 w-16 rounded-full bg-[#133322] text-[#f7f7f4] flex items-center justify-center mx-auto mb-6">
            <i className="fa-solid fa-check text-2xl"></i>
          </div>
          <h3 className="font-display text-3xl text-[#133322]">Solicitud recibida</h3>
          <p className="mt-4 text-[#5c6b61]">Hemos recibido tu solicitud. Nuestro equipo comercial te contactará en menos de 48 horas para iniciar el proceso de evaluación.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button onClick={() => setSubmitted(false)} className="eu-btn-outline">Enviar otra solicitud</button>
            {isModal && onClose && (<button onClick={onClose} className="eu-btn-primary">Cerrar</button>)}
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className={`${isModal ? "" : "eu-card max-w-4xl mx-auto p-8 lg:p-12"}`} data-testid="distribuidores-form">
          {!isModal && <div className="eu-data-label mb-6">FORMULARIO DE SOLICITUD</div>}

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Nombre completo *</label>
              <input required value={form.name} onChange={upd("name")} className={inputCls} data-testid="dist-name" />
            </div>
            <div>
              <label className={labelCls}>Email *</label>
              <input required type="email" value={form.email} onChange={upd("email")} className={inputCls} data-testid="dist-email" />
            </div>
            <div>
              <label className={labelCls}>Teléfono (con prefijo país) *</label>
              <input required placeholder="+34 600 000 000" value={form.phone} onChange={upd("phone")} className={inputCls} data-testid="dist-phone" />
            </div>
            <div>
              <label className={labelCls}>País *</label>
              <select required value={form.country} onChange={upd("country")} className={inputCls} data-testid="dist-country">
                <option value="">— Selecciona país —</option>
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Nombre de la empresa *</label>
              <input required value={form.company_name} onChange={upd("company_name")} className={inputCls} data-testid="dist-company" />
            </div>
            <div>
              <label className={labelCls}>Sitio web</label>
              <input placeholder="https://" value={form.company_website} onChange={upd("company_website")} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Años en el sector agrícola</label>
              <select value={form.years_in_sector} onChange={upd("years_in_sector")} className={inputCls}>
                <option value="">—</option>
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Volumen mensual estimado (kg)</label>
              <select value={form.estimated_volume_kg} onChange={upd("estimated_volume_kg")} className={inputCls}>
                <option value="">—</option>
                {VOLUME_RANGES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Portafolio de productos actual</label>
              <textarea rows={3} value={form.current_portfolio} onChange={upd("current_portfolio")} className={inputCls} placeholder="Marcas y categorías que ya distribuyes" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Canales de distribución</label>
              <div className="flex flex-wrap gap-2">
                {DIST_CHANNELS.map(ch => {
                  const active = form.distribution_channels.includes(ch);
                  return (
                    <button type="button" key={ch} onClick={() => toggleChannel(ch)}
                            className={`text-xs px-4 py-2 rounded-full border transition ${active ? "bg-[#133322] text-[#f7f7f4] border-[#133322]" : "bg-white border-[#e5e8e2] text-[#5c6b61] hover:border-[#133322]"}`}
                            data-testid={`dist-channel-${ch}`}>
                      {active && <i className="fa-solid fa-check text-[10px] mr-1.5"></i>}
                      {ch}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Zona geográfica de cobertura *</label>
              <textarea required rows={2} value={form.coverage_zone} onChange={upd("coverage_zone")} className={inputCls} placeholder="Regiones, provincias o países que cubres" data-testid="dist-coverage" />
            </div>
            <div>
              <label className={labelCls}>¿Cómo nos conociste?</label>
              <select value={form.how_did_you_know} onChange={upd("how_did_you_know")} className={inputCls}>
                <option value="">—</option>
                {HOW.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Mensaje adicional</label>
              <textarea rows={3} value={form.extra_message} onChange={upd("extra_message")} className={inputCls} />
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
            <p className="text-xs text-[#5c6b61] max-w-md">Al enviar aceptas que EUZERONE procese tus datos para evaluar tu solicitud de distribución.</p>
            <button type="submit" disabled={busy} className="eu-btn-primary disabled:opacity-50" data-testid="dist-submit">
              <i className="fa-solid fa-paper-plane text-xs"></i>
              {busy ? "Enviando…" : "Solicitar distribución"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function Distribuidores() {
  return (
    <section id="distribuye" className="py-24 lg:py-32 bg-[#f7f7f4]" data-testid="distribuidores-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <DistribuidoresContent variant="section" />
      </div>
    </section>
  );
}
